import {ChevronDownIcon, ChevronUpIcon} from '@heroicons/react/outline';
import type {JSX} from 'react';
import {useState} from 'react';

import {Button, TableSkeleton} from '@/components';

import type {ParameterStoreTableProps} from './types';

/**
 * The parameter store is served flat, with dots in the keys; this is the tree
 * `normalizeParams` folds it into. A leaf is the flat value, a branch another
 * level of the same shape.
 */
type NestedParams = {[key: string]: string | number | boolean | NestedParams};

function normalizeParams(
  flat: Record<string, string | number | boolean>,
): NestedParams {
  const nested: NestedParams = {};

  for (const key in flat) {
    const keys = key.split('.');
    let current = nested;

    keys.forEach((k, index) => {
      if (index === keys.length - 1) {
        current[k] = flat[key];
      } else {
        const child = current[k] || {};
        current[k] = child;
        current = child as NestedParams;
      }
    });
  }

  return nested;
}

export default function ParameterStoreTable({
  type,
  data,
  loading,
}: ParameterStoreTableProps) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  if (loading) {
    return <TableSkeleton />;
  }

  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="flex items-center gap-1 justify-center mt-5">
        <p className="text-gray-800 dark:text-white">No Data found.</p>
      </div>
    );
  }

  const nested = normalizeParams(data);
  // `type` names a top-level group, so its value is a branch, not a leaf.
  const params = nested[type] as NestedParams;

  const toggleExpand = (key: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderSections = (obj: NestedParams, parentKey = ''): JSX.Element[] => {
    const elements: JSX.Element[] = [];

    for (const [key, value] of Object.entries(obj)) {
      const rowKey = parentKey ? `${parentKey}.${key}` : key;
      const isExpandable = typeof value === 'object' && value !== null;
      const isExpanded = expandedRows[rowKey];

      if (isExpandable) {
        elements.push(
          <div
            key={rowKey}
            className="py-2 px-3 flex items-center justify-between bg-gray-100 dark:bg-gray-900 rounded"
          >
            <p className="text-slate-500 text-sm dark:text-slate-100">{key}</p>
            <div className="text-cyan-600 text-sm font-medium inline-flex self-start justify-start items-start gap-1 w-1/2 text-left truncate">
              <Button
                variant="unStyled"
                className="text-cyan-700"
                onClick={() => toggleExpand(rowKey)}
              >
                {isExpanded ? (
                  <>
                    Hide <ChevronUpIcon className="ms-1 w-4 h-4" />
                  </>
                ) : (
                  <>
                    Show <ChevronDownIcon className="ms-1 w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </div>,
        );

        if (isExpanded) {
          elements.push(
            <div
              key={`${rowKey}-expanded`}
              className="ml-4 mt-2 space-y-2 border-l border-gray-300 dark:border-gray-700 pl-4"
            >
              {renderSections(value, rowKey)}
            </div>,
          );
        }
      } else {
        elements.push(
          <div
            key={rowKey}
            className="py-2 px-3 flex items-center justify-between bg-gray-100 dark:bg-gray-900 rounded"
          >
            <p className="text-slate-500 text-sm dark:text-slate-100">{key}</p>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-100 w-1/2 text-left truncate">
              {String(value)}
            </p>
          </div>,
        );
      }
    }

    return elements;
  };

  return <div className="space-y-3">{renderSections(params)}</div>;
}
